import { useState, useCallback, useEffect, useMemo } from 'react';
import { 
  type AppState, 
  type ScoringItem, 
  type Criterion, 
  type DecisionContext, 
  type ScoringScale, 
  type ScoreMatrix,
  type WeightedResult 
} from './types';
import { ContextStep } from './components/ContextStep';
import { ItemsStep } from './components/ItemsStep';
import { CriteriaStep } from './components/CriteriaStep';
import { WeightsStep } from './components/WeightsStep';
import { ScaleStep } from './components/ScaleStep';
import { ScoringStep } from './components/ScoringStep';
import { ResultsStep } from './components/ResultsStep';
import { runWeightedScoring } from './utils/scoring';
import { AlertTriangle, Calculator, CheckCircle2 } from 'lucide-react';
import ToolWrapper, { type ToolHostProps, type ToolResultItem } from '../../components/prioritization/ToolWrapper';
import { useFormDraft } from '../../hooks/useFormDraft';
import { useToolMode, useToolSettings, useToolSituationalContext, type ToolItem } from '../../contexts/toolItemsHooks';
import { useLiveToolSession } from '../../components/prioritization/session/liveExperience/useLiveToolSession';
import { LiveToolScaffold } from '../../components/prioritization/session/liveExperience/LiveToolScaffold';
import { LiveScaleScore } from '../../components/prioritization/session/liveExperience/liveTools/LiveScaleScore';
import { useAttributesStore } from '../../stores/prioritization/attributesStore';
import { useSessionStore } from '../../stores/prioritization/sessionStore';
import { resolveSessionScoringCriteria, WEIGHTED_SCORING_DEFAULT_CRITERIA } from '../../lib/sessionScoringCriteria';
import { CriterionReadingHint } from '../../components/prioritization/values/CriterionReadingHint';

/**
 * Only for a live run that declared no criteria anywhere — see
 * WEIGHTED_SCORING_DEFAULT_CRITERIA in lib/sessionScoringCriteria (the Session
 * app declares this same set on the session when the host picks this method).
 */
const LIVE_DEFAULT_CRITERIA: Criterion[] = WEIGHTED_SCORING_DEFAULT_CRITERIA;

/** What a half-finished scoring run needs to come back to. */
interface ScoringDraft {
  scoreMatrix: ScoreMatrix;
  liveItemIndex: number;
  liveCriterionIndex: number;
}

function SimpleWeightedScoringContent(props: ToolHostProps) {
  const toolMode = useToolMode();
  const getAttributeByKey = useAttributesStore(state => state.getAttributeByKey);
  const { isLiveSession, importSourceLabel } = useLiveToolSession();
  const isAppMode = toolMode === 'app';
  const situationalContext = useToolSituationalContext();
  const completeLabel = situationalContext?.completion?.label ?? 'Save Session Result';

  // The criteria this sitting actually declared, weights included. Resolved
  // criteria_config → mode_config.v3Config → this tool's floor, because the
  // engine's mirror into criteria_config is a separate deferred write that does
  // not always land (see lib/sessionScoringCriteria). Reading only the first
  // step is what put Impact/Effort/Risk in front of a room that had declared
  // Urgency and Theme.
  const sessionCriteriaConfig = useSessionStore((store) => {
    const sid = situationalContext?.sessionId;
    if (!sid) return null;
    return store.sessions.find((x) => x.id === sid)?.criteriaConfig ?? null;
  });
  const sessionModeConfig = useSessionStore((store) => {
    const sid = situationalContext?.sessionId;
    if (!sid) return null;
    return store.sessions.find((x) => x.id === sid)?.modeConfig ?? null;
  });
  const hasSession = Boolean(situationalContext?.sessionId);
  // A guest cannot read the session row; the guest room hands over the same
  // criteria instead (migration 964). Without it a guest was told the session
  // had declared no criteria when it had.
  const readout = situationalContext?.sessionReadout;
  const resolvedCriteria = useMemo(() => {
    const carrier =
      readout && !sessionCriteriaConfig && !sessionModeConfig
        ? {
            criteriaConfig: readout.criteriaConfig ?? null,
            modeConfig: { v3Config: { criteria: readout.engineCriteria ?? [] } },
          }
        : hasSession
          ? { criteriaConfig: sessionCriteriaConfig, modeConfig: sessionModeConfig }
          : null;
    return resolveSessionScoringCriteria(
      carrier,
      LIVE_DEFAULT_CRITERIA,
      (id) => readout?.criterionLabels?.[id] ?? getAttributeByKey(id),
    );
  }, [hasSession, readout, sessionCriteriaConfig, sessionModeConfig, getAttributeByKey]);
  const criteriaSource = resolvedCriteria.source;
  const sessionCriteria: Criterion[] = resolvedCriteria.criteria;
  const [liveItemIndex, setLiveItemIndex] = useState(0);
  const [liveCriterionIndex, setLiveCriterionIndex] = useState(0);
  
  // In app mode, start at criteria (items come from import)
  // In standalone mode, start at context
  const [state, setState] = useState<AppState>(isAppMode ? 'criteria' : 'context');
  const [context, setContext] = useState<DecisionContext | null>(null);
  const [items, setItems] = useState<ScoringItem[]>([]);
  const [criteria, setCriteria] = useState<Criterion[]>([]);
  // The scale the session chose at setup (lib/toolSettings), 1–5 when none was
  // chosen. In a session everyone scores on it — the screen used to force 1–5
  // whatever the setup said. Outside a live session it is where the scale step
  // starts, and the person can still change it there.
  const { scoringScale: sessionScale } = useToolSettings();
  const [scale, setScale] = useState<ScoringScale>({ ...sessionScale, higherIsBetter: true });
  const liveScale = useMemo<ScoringScale>(() => ({ ...sessionScale, higherIsBetter: true }), [sessionScale]);
  const activeScale = isLiveSession ? liveScale : scale;
  const [scoreMatrix, setScoreMatrix] = useState<ScoreMatrix>({});
  const [results, setResults] = useState<WeightedResult[]>([]);

  /**
   * Scoring in a session used to live only in this component's memory. Ninety
   * answers — eighteen items across five criteria — were held in `scoreMatrix`
   * until the person pressed the complete button, and a refresh, a closed
   * laptop or a stray navigation threw all of it away with no warning and
   * nothing to come back to. Proven in the walkthrough: 89 entered, reload,
   * back to zero.
   *
   * useFormDraft is the repo's DB-first autosave for exactly this. Only inside
   * a session, where the key is stable and the work belongs to something; a
   * standalone run has no identity to file a draft under. Best-effort by
   * contract — if the draft cannot be written the tool behaves exactly as it
   * did before.
   */
  const draftSessionId = situationalContext?.sessionId ?? null;
  const scoringDraft = useFormDraft<ScoringDraft>({
    draftKey: `simple-weighted-scoring:${draftSessionId ?? 'standalone'}`,
    value: { scoreMatrix, liveItemIndex, liveCriterionIndex },
    enabled: Boolean(draftSessionId),
    isEmpty: (v) => Object.keys(v.scoreMatrix ?? {}).length === 0,
    onHydrate: (v) => {
      if (v.scoreMatrix && Object.keys(v.scoreMatrix).length > 0) setScoreMatrix(v.scoreMatrix);
      if (typeof v.liveItemIndex === 'number') setLiveItemIndex(v.liveItemIndex);
      if (typeof v.liveCriterionIndex === 'number') setLiveCriterionIndex(v.liveCriterionIndex);
    },
  });
  
  // Track source IDs for imported items
  const [sourceIdMap, setSourceIdMap] = useState<Record<string, string>>({});
  
  // Handler for importing items from workspace (app mode)
  const handleImportItems = useCallback((toolItems: ToolItem[]) => {
    const newItems: ScoringItem[] = toolItems.map(item => ({
      id: item.id,
      name: item.name,
    }));
    
    // Track source IDs
    const newSourceIdMap: Record<string, string> = {};
    toolItems.forEach(item => {
      if (item.sourceId) {
        newSourceIdMap[item.id] = item.sourceId;
      }
    });
    setSourceIdMap(prev => ({ ...prev, ...newSourceIdMap }));
    
    setItems(prev => [...prev, ...newItems]);

    if (isLiveSession && newItems.length > 0) {
      setCriteria(sessionCriteria);
      setLiveItemIndex(0);
      setLiveCriterionIndex(0);
      setState('scoring');
    }
  }, [isLiveSession, sessionCriteria]);
  
  // Get results for saving as session result
  const handleGetResults = useCallback((): ToolResultItem[] => {
    return results.map((result, idx) => ({
      id: sourceIdMap[result.itemId] || result.itemId,
      name: result.itemName,
      rank: idx + 1,
      score: result.totalScore,
      scoreBreakdown: Object.fromEntries(
        Object.entries(result.criterionScores).map(([criterionId, scoreDetail]) => [
          criterionId,
          scoreDetail.weightedContribution,
        ])
      ),
      metadata: {
        criterionScores: result.criterionScores,
        // Where the scored dimensions came from, stored with the submission.
        // A `results` blob keyed impact/effort/risk looks perfectly well-formed
        // next to a session whose criteria were something else entirely; the
        // close pipeline has no other way to tell the two apart.
        criteriaSource,
        criteria: criteria.map((c) => ({ id: c.id, name: c.name, weight: c.weight })),
      }
    }));
  }, [results, sourceIdMap, criteriaSource, criteria]);
  
  /**
   * The draft is the person's safety net until their answers are somewhere
   * durable, so it is dropped only after the host's submission resolves — not
   * when results are merely computed on screen, which is before anything has
   * been recorded.
   */
  const hostCompleteResults = props.onCompleteResults;
  const handleCompleteResults = useCallback(
    async (toolResults: ToolResultItem[]) => {
      if (!hostCompleteResults) return;
      await hostCompleteResults(toolResults);
      await scoringDraft.discard();
    },
    [hostCompleteResults, scoringDraft],
  );

  // Check if results are ready
  const hasResults = results.length > 0;

  const liveScoringComplete =
    isLiveSession &&
    items.length > 0 &&
    criteria.length > 0 &&
    items.every(item => criteria.every(c => scoreMatrix[item.id]?.[c.id] != null));

  // In a session the result is computed as soon as every score is in, and again
  // after any change — not only when Done is pressed. Submit used to appear at
  // that moment, send an empty result and be refused ("Finish rating all items")
  // until the person found Done.
  useEffect(() => {
    if (!liveScoringComplete) return;
    let cancelled = false;
    void runWeightedScoring(items, criteria, scoreMatrix, activeScale).then((computed) => {
      if (!cancelled) setResults(computed);
    });
    return () => {
      cancelled = true;
    };
  }, [liveScoringComplete, items, criteria, scoreMatrix, activeScale]);

  // The progress line used to read the cursor's position
  // (liveItemIndex * criteria.length + liveCriterionIndex), not how many
  // answers exist. On the last cell that is (n-1) * criteria + (criteria - 1),
  // one short of the total, and the cursor never moves past it — so a
  // participant who had answered every cell was told "89 of 90 items placed,
  // 99% complete" and reasonably concluded they were stuck. Count the answers.
  const liveScoredCount = useMemo(
    () =>
      items.reduce(
        (total, item) =>
          total + criteria.filter((c) => scoreMatrix[item.id]?.[c.id] != null).length,
        0,
      ),
    [items, criteria, scoreMatrix],
  );

  const liveCurrentItem = items[liveItemIndex];
  const liveCurrentCriterion = criteria[liveCriterionIndex];
  const liveScoreValue =
    liveCurrentItem && liveCurrentCriterion
      ? scoreMatrix[liveCurrentItem.id]?.[liveCurrentCriterion.id] ?? null
      : null;

  const handleLiveScore = (value: number) => {
    if (!liveCurrentItem || !liveCurrentCriterion) return;
    handleScoreChange(liveCurrentItem.id, liveCurrentCriterion.id, value);
  };

  const handleLiveNext = async () => {
    if (liveCriterionIndex < criteria.length - 1) {
      setLiveCriterionIndex(liveCriterionIndex + 1);
    } else if (liveItemIndex < items.length - 1) {
      setLiveItemIndex(liveItemIndex + 1);
      setLiveCriterionIndex(0);
    } else {
      setResults(await runWeightedScoring(items, criteria, scoreMatrix, activeScale));
    }
  };

  const handleLiveBack = () => {
    if (liveCriterionIndex > 0) {
      setLiveCriterionIndex(liveCriterionIndex - 1);
    } else if (liveItemIndex > 0) {
      setLiveItemIndex(liveItemIndex - 1);
      setLiveCriterionIndex(criteria.length - 1);
    }
  };

  const handleContextNext = (newContext: DecisionContext) => {
    setContext(newContext);
    setState('items');
  };

  const handleContextSkip = () => {
    setState('items');
  };

  const handleItemsNext = (newItems: ScoringItem[]) => {
    setItems(newItems);
    setState('criteria');
  };

  const handleCriteriaNext = (newCriteria: Criterion[]) => {
    setCriteria(newCriteria);
    setState('weights');
  };

  const handleWeightsNext = (newCriteria: Criterion[]) => {
    setCriteria(newCriteria);
    setState('scale');
  };

  const handleScaleNext = (newScale: ScoringScale) => {
    setScale(newScale);
    setState('scoring');
  };

  const handleScoreChange = (itemId: string, criterionId: string, score: number) => {
    setScoreMatrix((prev) => ({
      ...prev,
      [itemId]: {
        ...prev[itemId],
        [criterionId]: score,
      },
    }));
  };

  const handleScoringNext = async () => {
    setResults(await runWeightedScoring(items, criteria, scoreMatrix, scale));
    setState('results');
  };

  const handleReset = () => {
    setState(isAppMode ? 'criteria' : 'context');
    setContext(null);
    if (!isAppMode) {
      setItems([]);
    }
    setCriteria([]);
    setScale({ ...sessionScale, higherIsBetter: true });
    setScoreMatrix({});
    setResults([]);
  };

  const getStepNumber = (): number => {
    const steps: AppState[] = isAppMode 
      ? ['criteria', 'weights', 'scale', 'scoring', 'results']
      : ['context', 'items', 'criteria', 'weights', 'scale', 'scoring', 'results'];
    return steps.indexOf(state) + 1;
  };

  const getTotalSteps = (): number => {
    return isAppMode ? 5 : 7;
  };

  return (
    <ToolWrapper
      toolName="Simple Weighted Scoring"
      toolDescription="Prioritize items using weighted criteria scoring"
      onImportItems={handleImportItems}
      itemCount={items.length}
      onGetResults={hasResults ? handleGetResults : undefined}
      {...props}
      onCompleteResults={hostCompleteResults ? handleCompleteResults : undefined}
    >
      {isLiveSession && state === 'scoring' && liveCurrentItem && liveCurrentCriterion && (
        <LiveToolScaffold
          title="Weighted scoring"
          subtitle={`${liveCurrentCriterion.name} — item ${liveItemIndex + 1} of ${items.length}`}
          progressDone={liveScoredCount}
          progressTotal={items.length * criteria.length}
        >
          {criteriaSource === 'tool_default' && (
            <div
              role="status"
              className="mb-4 flex items-start gap-2 rounded-lg border border-[var(--mustard)] bg-[var(--mustard-dim)] px-4 py-3 font-body text-[0.8rem] font-light text-[#D8DDE8]"
            >
              <AlertTriangle className="mt-0.5 h-4 w-4 shrink-0 text-[var(--mustard)]" />
              <span>
                <strong>This session hasn’t declared its criteria.</strong> You’re scoring{' '}
                {criteria.map((c) => c.name).join(', ')} — the tool’s standard dimensions, not this
                session’s. Tell your facilitator if that isn’t what was asked.
              </span>
            </div>
          )}
          <LiveScaleScore
            itemName={liveCurrentItem.name}
            itemIndex={liveItemIndex}
            totalItems={items.length}
            phaseTitle={liveCurrentCriterion.name}
            criterionReading={<CriterionReadingHint attributeKey={liveCurrentCriterion.id} />}
            valueDefinition={
              getAttributeByKey(liveCurrentCriterion.id)?.definition ||
              liveCurrentCriterion.description ||
              undefined
            }
            phaseDescription={`Score ${liveScale.min}–${liveScale.max} (${liveCurrentCriterion.weight}% weight)`}
            min={liveScale.min}
            max={liveScale.max}
            value={liveScoreValue}
            onValueChange={handleLiveScore}
            onNext={handleLiveNext}
            onBack={
              liveItemIndex > 0 || liveCriterionIndex > 0 ? handleLiveBack : undefined
            }
            isLastStep={
              liveItemIndex === items.length - 1 &&
              liveCriterionIndex === criteria.length - 1
            }
          />
        </LiveToolScaffold>
      )}

      {!isLiveSession && (
      <div className="space-y-6">
        {/* Progress bar and step indicator */}
        {((isAppMode && items.length > 0) || !isAppMode) && state !== 'results' && (
          <div className="card">
            <div className="flex items-center justify-between mb-2">
              <span className="text-sm font-medium text-[var(--text-sec)]">
                Step {getStepNumber()} of {getTotalSteps()}
              </span>
              {items.length > 0 && (
                <span className="text-sm text-[var(--text-ter)]">
                  {items.length} items to score
                </span>
              )}
            </div>
            <div className="w-full bg-[var(--hover-tint)] rounded-full h-2">
              <div
                className="bg-[var(--slate)] h-2 rounded-full transition-all duration-300"
                style={{ width: `${(getStepNumber() / getTotalSteps()) * 100}%` }}
              />
            </div>
          </div>
        )}

        {/* Main Content */}
        <div className="card">
          {/* Context step - standalone only */}
          {!isAppMode && state === 'context' && (
            <ContextStep
              context={context}
              onNext={handleContextNext}
              onSkip={handleContextSkip}
            />
          )}

          {/* Items step - standalone only */}
          {!isAppMode && state === 'items' && (
            <ItemsStep items={items} onNext={handleItemsNext} />
          )}

          {/* Criteria step */}
          {state === 'criteria' && (
            <>
              {isAppMode && items.length === 0 ? (
                <div className="text-center py-12 text-[var(--text-ter)]">
                  <Calculator className="h-12 w-12 mx-auto mb-4 text-[var(--text-ter)]" />
                  <p>{`Import items from the ${importSourceLabel} to continue`}</p>
                </div>
              ) : (
                <CriteriaStep criteria={criteria} onNext={handleCriteriaNext} />
              )}
            </>
          )}

          {state === 'weights' && (
            <WeightsStep criteria={criteria} onNext={handleWeightsNext} />
          )}

          {state === 'scale' && (
            <ScaleStep scale={scale} onNext={handleScaleNext} />
          )}

          {state === 'scoring' && (
            <ScoringStep
              items={items}
              criteria={criteria}
              scoreMatrix={scoreMatrix}
              scale={scale}
              onScoreChange={handleScoreChange}
              onNext={handleScoringNext}
            />
          )}

          {state === 'results' && (
            <ResultsStep
              results={results}
              criteria={criteria}
              context={context}
              scale={scale}
              scoreMatrix={scoreMatrix}
              onReset={handleReset}
            />
          )}
        </div>
        
        {/* Completion prompt for app mode */}
        {hasResults && isAppMode && (
          <div className="card bg-[var(--green-dim)] border-[var(--green)]">
            <div className="flex items-center gap-3">
              <CheckCircle2 className="h-6 w-6 text-[var(--green)] flex-shrink-0" />
              <div>
                <h3 className="font-semibold text-[var(--green)] dark:text-[var(--text-ter)]">Scoring complete!</h3>
                <p className="text-sm text-[var(--green)] dark:text-[var(--text-ter)]">
                  Click <strong>"{completeLabel}"</strong> in the header to save your scored priorities.
                </p>
              </div>
            </div>
          </div>
        )}
      </div>
      )}
    </ToolWrapper>
  );
}

export default function SimpleWeightedScoring(props: ToolHostProps) {
  return <SimpleWeightedScoringContent {...props} />;
}
